import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PageHeader from '../components/PageHeader';
import IngredientLibrary from '../components/formulas/IngredientLibrary';
import MethodsLibrary from '../components/lab/MethodsLibrary';
import FormulaList from '../components/lab/FormulaList';
import FormulaEditor from '../components/lab/FormulaEditor';
import { GridIcon, FlaskIcon } from '../components/icons';
import { StepsIcon } from '../components/icons';
import { listLabFormulas, listMethods, listProductTypes, listBatches } from '../lib/labData';
import { createFamilyWithVersion } from '../lib/formulas';
import './Lab.css';

const TABS = [
  { id: 'Formulas', label: 'Formulas', Icon: GridIcon },
  { id: 'Ingredients', label: 'Ingredients', Icon: FlaskIcon },
  { id: 'Methods', label: 'Methods', Icon: StepsIcon },
];

// The Lab: what you make and what you make it from. Everything about
// actually making a batch lives in Production — the split is deliberate, so
// neither section grows its own half of the other.
export default function Lab() {
  const navigate = useNavigate();
  const [tab, setTab] = useState(TABS[0].id);
  const [formulas, setFormulas] = useState([]);
  const [productTypes, setProductTypes] = useState([]);
  const [methods, setMethods] = useState([]);
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [adding, setAdding] = useState(false);

  const loadAll = useCallback(async () => {
    setLoadError(null);
    try {
      const [f, t, m, b] = await Promise.all([
        listLabFormulas(),
        listProductTypes(),
        listMethods(),
        listBatches(),
      ]);
      setFormulas(f);
      setProductTypes(t);
      setMethods(m);
      setBatches(b);
    } catch (err) {
      setLoadError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const handleCreate = async (versionFields, ingredients, changeNote, familyExtras) => {
    const family = await createFamilyWithVersion(
      {
        name: versionFields.product_name,
        product_type: versionFields.product_type,
        product_type_id: familyExtras?.product_type_id || null,
        status: 'Active',
      },
      versionFields,
      ingredients,
      changeNote
    );
    setAdding(false);
    await loadAll();
    if (family?.id) navigate(`/lab/formulas/${family.id}`);
  };

  return (
    <>
      <PageHeader
        title="Lab"
        subtitle="Formulas, ingredients and the methods used to make them."
      />

      <div className="view-tabs">
        {TABS.map(({ id, label, Icon }) => (
          <button
            key={id}
            className={'view-tab' + (tab === id ? ' active' : '')}
            onClick={() => setTab(id)}
          >
            <Icon /> {label}
          </button>
        ))}
      </div>

      {loadError && <p className="formula-error">Couldn't load the lab: {loadError}</p>}

      {tab === 'Formulas' &&
        (adding ? (
          <section className="panel">
            <h3 className="panel-title">New formula</h3>
            <FormulaEditor
              productTypes={productTypes}
              onSubmit={handleCreate}
              onCancel={() => setAdding(false)}
            />
          </section>
        ) : (
          <FormulaList
            formulas={formulas}
            batches={batches}
            productTypes={productTypes}
            loading={loading}
            onAdd={() => setAdding(true)}
            onOpen={(f) => navigate(`/lab/formulas/${f.id}`)}
            onChanged={loadAll}
          />
        ))}

      {tab === 'Ingredients' && <IngredientLibrary formulas={formulas} />}

      {tab === 'Methods' && (
        <MethodsLibrary
          methods={methods}
          productTypes={productTypes}
          formulas={formulas}
          onChanged={loadAll}
        />
      )}
    </>
  );
}
